import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSku } from '../lib/catalog.js';
import { useMe } from '../lib/auth.js';
import { useCart } from '../lib/cart.js';
import { ApiError } from '../lib/api.js';
import { bindingTypeLabel, productLabel } from '@z9-store/core/labels';
import type { Sku } from '@z9-store/core';

export function ProductDetailPage(): JSX.Element {
  const params = useParams<{ productId: string }>();
  const q = useSku(params.productId);
  if (q.isLoading) return <p className="text-sm text-slate-500">Loading...</p>;
  if (q.error instanceof ApiError && q.error.status === 404) return <NotFound />;
  if (q.isError) {
    return (
      <div className="rounded bg-red-50 p-4 text-sm text-red-700" data-testid="product-error">
        Could not load: {(q.error as Error).message}
      </div>
    );
  }
  const sku = q.data;
  if (!sku) return <NotFound />;
  return <Detail sku={sku} />;
}

function Detail({ sku }: { sku: Sku }): JSX.Element {
  const me = useMe();
  const cart = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const next = encodeURIComponent(location.pathname + location.search);
  const inCart = cart.items.some((i) => i.skuId === sku.skuId);
  // "License type" combines the catalog tier (Product license / Feature
  // license, normalized to singular) with a Renewable suffix when the SKU
  // is renewable. Non-renewable SKUs just show the tier.
  const tierSingular = sku.tier.replace(/\s+licenses?$/i, ' license');
  const licenseType = sku.renewable ? `${tierSingular} - Renewable` : tierSingular;
  return (
    <article data-testid="product-detail" className="space-y-6">
      <header className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{sku.tier}</p>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="product-display-name">
          {sku.displayName}
        </h1>
        <p className="text-sm text-slate-600">{sku.description}</p>
      </header>
      <dl className="grid grid-cols-1 gap-4 rounded-lg border border-slate-200 bg-white p-4 text-sm sm:grid-cols-2">
        <Field label="Product" value={productLabel(sku.product)} />
        <Field label="Renewal term" value={`${sku.renewalTermMonths} months`} />
        <Field label="License type" value={licenseType} />
        <Field label="Binding" value={bindingTypeLabel(sku.bindingType)} />
      </dl>
      <div className="rounded border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
        {me.data ? (
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={inCart}
              onClick={() => {
                cart.add({ skuId: sku.skuId, displayName: sku.displayName, bindingType: sku.bindingType });
              }}
              className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
              data-testid="product-add-to-cart"
            >
              {inCart ? 'In cart' : 'Add to cart'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!inCart) cart.add({ skuId: sku.skuId, displayName: sku.displayName, bindingType: sku.bindingType });
                navigate('/cart');
              }}
              className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-900 hover:bg-slate-100"
              data-testid="product-go-to-cart"
            >
              Go to cart
            </button>
          </div>
        ) : (
          <p data-testid="product-buy-signin">
            <Link to={`/login?next=${next}`} className="font-medium text-slate-900 underline">
              Sign in
            </Link>{' '}
            to add this to a cart and check out.
          </p>
        )}
      </div>
    </article>
  );
}

function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="mt-1 text-slate-900">{value}</dd>
    </div>
  );
}

function NotFound(): JSX.Element {
  return (
    <div data-testid="product-not-found" className="rounded bg-slate-100 p-6 text-sm text-slate-700">
      That product does not exist or is not currently published.{' '}
      <Link to="/" className="font-medium text-slate-900 underline">
        Back to catalog
      </Link>
      .
    </div>
  );
}
